import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  Divider,
} from '@mui/material';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import { refundEvent } from '../../services/paymentsService';
import { canRefundPayments } from '../../utils/paymentUtils';
import { getSaleProducts } from '../../utils/salesUtils';
import RefundNotificationFields from '../refunds/RefundNotificationFields';

/**
 * Event bazlı toplu iade aksiyonu (sales listesinde eventID ile filtreliyken görünür).
 * - Tüm satışları tamamen iade
 * - Kategori bazlı (ör. tüm localar)
 * - Ürün bazlı
 */
const BulkRefundActions = ({ eventID, sales = [], onDone }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scope, setScope] = useState('all'); // 'all' | 'category' | 'product'
  const [groupKey, setGroupKey] = useState('');
  const [productKey, setProductKey] = useState(''); // 'groupKey::productId'
  const [reasonMail, setReasonMail] = useState('');
  const [reasonSms, setReasonSms] = useState('');
  const [reasonPush, setReasonPush] = useState('');

  const { categories, products } = useMemo(() => {
    const catMap = new Map();
    const prodMap = new Map();
    (sales || []).forEach((sale) => {
      if (Number(sale.status) !== 1) return;
      getSaleProducts(sale).forEach((p) => {
        catMap.set(p.groupKey, (catMap.get(p.groupKey) ?? 0) + p.count);
        const pk = `${p.groupKey}::${p.productId}`;
        const prev = prodMap.get(pk) ?? { ...p, count: 0 };
        prodMap.set(pk, { ...prev, count: prev.count + p.count });
      });
    });
    return {
      categories: Array.from(catMap.entries()).map(([key, count]) => ({ key, count })),
      products: Array.from(prodMap.entries()).map(([pk, p]) => ({ ...p, pk })),
    };
  }, [sales]);

  const eventName = useMemo(() => {
    const sale = (sales || []).find((s) => s?.event?.name || s?.eventName);
    return sale?.event?.name || sale?.eventName || '';
  }, [sales]);

  if (!eventID) return null;
  if (!canRefundPayments(permissions)) return null;

  const refundableCount = (sales || []).filter((s) => Number(s.status) === 1).length;

  const openDialog = () => {
    setScope('all');
    setGroupKey(categories[0]?.key ?? '');
    setProductKey(products[0]?.pk ?? '');
    setReasonMail('');
    setReasonSms('');
    setReasonPush('');
    setOpen(true);
  };

  const handleSubmit = async () => {
    const body = {
      eventId: eventID,
      refundReasonMail: reasonMail.trim() || undefined,
      refundReasonSms: reasonSms.trim() || undefined,
      refundReasonPush: reasonPush.trim() || undefined,
    };
    
    if (scope === 'category') {
      if (!groupKey) {
        notify('Bir kategori seçin.', { type: 'warning' });
        return;
      }
      body.groupKey = groupKey;
    } else if (scope === 'product') {
      const prod = products.find((p) => p.pk === productKey);
      if (!prod) {
        notify('Bir ürün seçin.', { type: 'warning' });
        return;
      }
      body.groupKey = prod.groupKey;
      body.productId = prod.productId;
    }

    if (!window.confirm("Bu işlem geri alınamaz. Emin misiniz?")) return;

    setSubmitting(true);
    try {
      const res = await refundEvent(body);
      notify(
        `Toplu iade tamamlandı. Başarılı: ${res?.refundedCount ?? 0}, Hata: ${res?.failedCount ?? 0}`,
        { type: 'info' },
      );
      setOpen(false);
      onDone?.();
    } catch (err) {
      notify(err?.message || 'Toplu iade sırasında hata oluştu.', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Button variant="outlined" color="warning" size="small" onClick={openDialog}>
        {translate('sales.bulk_refund_title', { _: 'Toplu İade İşlemleri' })}
      </Button>

      <Dialog open={open} onClose={submitting ? undefined : () => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('sales.bulk_refund_title', { _: 'Toplu İade İşlemleri' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
            Bu etkinlik için toplu iade başlatabilirsiniz.
          </Typography>

          <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)} row sx={{ mb: 2 }}>
            <FormControlLabel value="all" control={<Radio size="small" />} label="Tüm satışlar" />
            <FormControlLabel
              value="category"
              control={<Radio size="small" />}
              label="Kategori bazlı"
              disabled={categories.length === 0}
            />
            <FormControlLabel
              value="product"
              control={<Radio size="small" />}
              label="Ürün bazlı"
              disabled={products.length === 0}
            />
          </RadioGroup>

          {scope === 'category' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Kategori</InputLabel>
              <Select
                label="Kategori"
                value={groupKey}
                onChange={(e) => setGroupKey(e.target.value)}
              >
                {categories.map((c) => (
                  <MenuItem key={c.key} value={c.key}>
                    {c.key} ({c.count} adet)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {scope === 'product' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Ürün</InputLabel>
              <Select
                label="Ürün"
                value={productKey}
                onChange={(e) => setProductKey(e.target.value)}
              >
                {products.map((p) => (
                  <MenuItem key={p.pk} value={p.pk}>
                    {p.title} — {p.groupKey} ({p.count} adet)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Divider sx={{ my: 2 }} />

          <RefundNotificationFields
            reasonMail={reasonMail}
            reasonSms={reasonSms}
            reasonPush={reasonPush}
            onMailChange={setReasonMail}
            onSmsChange={setReasonSms}
            onPushChange={setReasonPush}
            eventName={eventName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            {translate('ra.action.cancel', { _: 'Vazgeç' })}
          </Button>
          <Button onClick={handleSubmit} color="warning" variant="contained" disabled={submitting}>
            {submitting ? '...' : translate('sales.bulk_refund_start', { _: 'İadeyi Başlat' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkRefundActions;
